import type { CartItem, MenuPage, Product, SelectedAlternative } from '@/lib/types'
import { isAvailable } from '@/lib/utils'

export function findCartProduct(pages: MenuPage[], item: CartItem): Product | undefined {
  return pages
    .flatMap((page) => page.categories.flatMap((category) => category.products))
    .find((product) => product.properties?.id === item.productId && product.properties.stateId === item.stateId && isAvailable(product))
}

function alternativesKey(alternatives: SelectedAlternative[]): string {
  return JSON.stringify(
    alternatives
      .map((alternative) => [
        alternative.alternativeGroupId,
        alternative.alternativeItemName,
        alternative.count,
        alternative.addonProduct?.productId ?? null,
        alternative.addonProduct?.productStateId ?? null,
        alternative.addonProduct?.productPrice ?? null,
      ])
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))),
  )
}

export function sameCartItem(a: CartItem, b: CartItem): boolean {
  return (
    a.productId === b.productId &&
    a.stateId === b.stateId &&
    a.price === b.price &&
    (a.message?.trim() ?? '') === (b.message?.trim() ?? '') &&
    alternativesKey(a.alternatives) === alternativesKey(b.alternatives)
  )
}

export function mergeCartItem(cart: CartItem[], item: CartItem): CartItem[] {
  const index = cart.findIndex((existing) => sameCartItem(existing, item))
  return index < 0 ? [...cart, item] : cart.map((existing, i) => (i === index ? { ...existing, count: existing.count + item.count } : existing))
}

export function createCartItem(product: Product, alternatives: SelectedAlternative[], message?: string, count = 1): CartItem | null {
  const properties = product.properties
  if (!properties) return null
  const price = (properties.price ?? 0) + alternatives.reduce((sum, alternative) => sum + (alternative.addonProduct?.productPrice ?? 0) * alternative.count, 0)
  if (!Number.isFinite(price) || price <= 0) return null
  return {
    productId: properties.id,
    stateId: properties.stateId,
    name: product.name,
    price,
    count,
    alternatives,
    alternativesSummary: alternatives.map((alternative) => alternative.alternativeItemName + (alternative.count > 1 ? ` x${alternative.count}` : '')).join(', '),
    imageUrl: properties.imageUrl,
    message: message?.trim() || undefined,
  }
}
